import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  ContentProviderAdapter,
  ContentRequestOptions,
} from "../core/content-provider.interface";
import {
  Article,
  ArticleListResponse,
  Category,
  CategoryPreview,
  GetArticlesParams,
  GetCategoriesParams,
  GetPreviewsParams,
  ArticleImage,
  Author,
} from "../models/learn-content.model";
import { REQUEST_API } from "src/app/shared/consts";

// Ghost-specific API response types
interface GhostPost {
  readonly id: string;
  readonly uuid: string;
  readonly title: string;
  readonly slug: string;
  readonly html?: string;
  readonly feature_image?: string;
  readonly feature_image_alt?: string;
  readonly feature_image_caption?: string;
  readonly excerpt?: string;
  readonly published_at?: string;
  readonly updated_at?: string;
  readonly reading_time?: number;
  readonly primary_author?: GhostAuthor;
  readonly tags?: readonly GhostTag[];
  readonly featured?: boolean;
}

interface GhostAuthor {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly profile_image?: string;
  readonly bio?: string;
}

interface GhostTag {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly description?: string;
  readonly feature_image?: string;
}

interface GhostPagination {
  readonly page: number;
  readonly limit: number;
  readonly pages: number;
  readonly total: number;
  readonly next: number | null;
  readonly prev: number | null;
}

interface GhostPostsResponse {
  readonly posts?: readonly (GhostPost | null)[] | null;
  readonly pagination?: GhostPagination | null;
}

interface GhostPostResponse {
  readonly post?: GhostPost | null;
}

interface GhostTagsResponse {
  readonly tags?: readonly (GhostTag | null)[] | null;
}

interface GhostTagResponse {
  readonly tag?: GhostTag | null;
}

interface GhostTagPreview {
  readonly tag?: GhostTag | null;
  readonly posts?: readonly (GhostPost | null)[] | null;
}

/**
 * Ghost CMS Content Adapter
 *
 * Implements ContentProviderAdapter for Ghost CMS.
 * Handles all Ghost-specific API calls and data transformations.
 * Maps Ghost data models (GhostPost, GhostTag) to our domain models (Article, Category).
 *
 * Note: searchArticles is not implemented - Ghost search requires separate configuration.
 */
@Injectable()
export class GhostContentAdapter implements ContentProviderAdapter {
  private readonly http: HttpClient;

  private readonly baseUrl = `${REQUEST_API}/learn`;

  constructor(...args: unknown[]);
  /* istanbul ignore next -- Angular DI migration wiring */
  constructor() {
    const [http = inject(HttpClient)] = arguments as unknown as [
      HttpClient | undefined,
    ];

    this.http = http;
  }

  /**
   * Retrieves a paginated list of articles.
   *
   * @param params - Parameters for filtering and pagination
   * @returns Observable of article list with pagination data
   */
  getArticles(params: GetArticlesParams): Observable<ArticleListResponse> {
    const request = {
      page: params.page,
      limit: params.pageSize,
      category: params.categorySlug,
    };

    // Hide loading spinner for first page (skeleton is shown instead)
    const headers = params.page === 1 ? { hideLoading: "true" } : {};

    return this.http
      .post<GhostPostsResponse>(`${this.baseUrl}/posts`, request, { headers })
      .pipe(map((response) => this.mapPostsResponseToArticleList(response)));
  }

  /**
   * Retrieves a single article by its slug.
   *
   * @param slug - The article slug
   * @param options - Optional tracking context (e.g., source screen name)
   * @returns Observable of article or null if not found
   */
  getArticleBySlug(
    slug: string,
    options?: ContentRequestOptions
  ): Observable<Article | null> {
    const params = this.buildTrackingParams(options);

    return this.http
      .get<GhostPostResponse>(`${this.baseUrl}/posts/slug/${slug}`, { params })
      .pipe(map((response) => this.mapPostToArticle(response?.post)));
  }

  /**
   * Retrieves a single article by its ID.
   *
   * @param id - The article ID
   * @param options - Optional tracking context (e.g., source screen name)
   * @returns Observable of article or null if not found
   */
  getArticleById(
    id: string,
    options?: ContentRequestOptions
  ): Observable<Article | null> {
    const params = this.buildTrackingParams(options);

    return this.http
      .get<GhostPostResponse>(`${this.baseUrl}/posts/${id}`, { params })
      .pipe(map((response) => this.mapPostToArticle(response?.post)));
  }

  /**
   * Retrieves a list of categories.
   *
   * @param params - Optional parameters for filtering
   * @returns Observable of categories array
   */
  getCategories(params?: GetCategoriesParams): Observable<readonly Category[]> {
    const request = {
      limit: params?.limit,
    };

    return this.http
      .post<GhostTagsResponse>(`${this.baseUrl}/tags`, request)
      .pipe(
        map((response) =>
          (response?.tags ?? [])
            .map((tag) => this.mapTagToCategory(tag))
            .filter((category): category is Category => category !== null)
        )
      );
  }

  /**
   * Retrieves a single category by its slug.
   *
   * @param slug - The category slug
   * @returns Observable of category or null if not found
   */
  getCategoryBySlug(slug: string): Observable<Category | null> {
    return this.http
      .get<GhostTagResponse>(`${this.baseUrl}/tags/slug/${slug}`)
      .pipe(map((response) => this.mapTagToCategory(response?.tag)));
  }

  /**
   * Retrieves a single category by its ID.
   *
   * @param id - The category ID
   * @returns Observable of category or null if not found
   */
  getCategoryById(id: string): Observable<Category | null> {
    return this.http
      .get<GhostTagResponse>(`${this.baseUrl}/tags/${id}`)
      .pipe(map((response) => this.mapTagToCategory(response?.tag)));
  }

  /**
   * Retrieves article previews grouped by category.
   *
   * @param params - Parameters for limiting articles and categories
   * @returns Observable of category previews with articles
   */
  getArticlePreviews(
    params: GetPreviewsParams
  ): Observable<readonly CategoryPreview[]> {
    const request = {
      postLimit: params.articlesPerCategory,
      tagsLimit: params.categoriesLimit,
    };

    return this.http
      .post<readonly GhostTagPreview[]>(`${this.baseUrl}/previews`, request, {
        headers: { hideLoading: "true" },
      })
      .pipe(
        map((previews) =>
          (previews ?? [])
            .map((preview) => {
              const category = this.mapTagToCategory(preview?.tag);
              if (!category) return null;

              const articles = (preview?.posts ?? [])
                .map((post) => this.mapPostToArticle(post))
                .filter((article): article is Article => article !== null);

              return { category, articles } as CategoryPreview;
            })
            .filter((preview): preview is CategoryPreview => preview !== null)
        )
      );
  }

  // ============================================
  // Query Parameter Helpers
  // ============================================

  /**
   * Builds query parameters for tracking context.
   * Converts ContentRequestOptions into HTTP query parameters for backend analytics.
   *
   * @param options - Optional tracking context
   * @returns Record of query parameters to include in the request
   */
  private buildTrackingParams(
    options?: ContentRequestOptions
  ): Record<string, string> {
    const params: Record<string, string> = {};

    if (options?.sourceScreenName) {
      params["source_screen_name"] = options.sourceScreenName;
    }

    return params;
  }

  // ============================================
  // Transformation Methods: Ghost → Domain Models
  // ============================================

  /**
   * Maps a Ghost post to our domain Article model.
   *
   * Transforms Ghost-specific post data structure into our standardized Article interface,
   * handling optional fields and nested object transformations.
   *
   * @param post - The Ghost post object to transform (may be null/undefined)
   * @returns Article object with mapped properties, or null if post is invalid
   */
  private mapPostToArticle(post: GhostPost | null | undefined): Article | null {
    if (!post) return null;

    return {
      id: post.id,
      title: post.title,
      slug: post.slug,
      content: post.html ?? "",
      excerpt: post.excerpt,
      featuredImage: this.mapFeaturedImage(post),
      author: this.mapAuthor(post.primary_author) ?? undefined,
      publishedAt: post.published_at ? new Date(post.published_at) : undefined,
      updatedAt: post.updated_at ? new Date(post.updated_at) : undefined,
      readingTime: post.reading_time,
      categories: post.tags
        ?.map((tag) => this.mapTagToCategory(tag))
        .filter((category): category is Category => category !== null),
      featured: post.featured,
    };
  }

  /**
   * Maps Ghost post featured image data to our ArticleImage model.
   *
   * Extracts and transforms featured image information from a Ghost post,
   * returning undefined if no featured image is present.
   *
   * @param post - The Ghost post containing featured image data
   * @returns ArticleImage object with mapped image properties, or undefined if no image
   */
  private mapFeaturedImage(post: GhostPost): ArticleImage | undefined {
    if (!post.feature_image) return undefined;

    return {
      url: post.feature_image,
      alt: post.feature_image_alt,
      caption: post.feature_image_caption,
    };
  }

  /**
   * Maps a Ghost author to our domain Author model.
   *
   * Transforms Ghost-specific author data structure into our standardized Author interface,
   * preserving all available author information including profile image and bio.
   *
   * @param ghostAuthor - The Ghost author object to transform (may be null/undefined)
   * @returns Author object with mapped properties, or null if author is invalid
   */
  private mapAuthor(
    ghostAuthor: GhostAuthor | null | undefined
  ): Author | null {
    if (!ghostAuthor) return null;

    return {
      id: ghostAuthor.id,
      name: ghostAuthor.name,
      slug: ghostAuthor.slug,
      profileImage: ghostAuthor.profile_image,
      bio: ghostAuthor.bio,
    };
  }

  /**
   * Maps a Ghost tag to our domain Category model.
   *
   * Transforms Ghost-specific tag data structure into our standardized Category interface.
   * In Ghost CMS, tags serve as categories for content organization.
   *
   * @param tag - The Ghost tag object to transform (may be null/undefined)
   * @returns Category object with mapped properties, or null if tag is invalid
   */
  private mapTagToCategory(tag: GhostTag | null | undefined): Category | null {
    if (!tag) return null;

    return {
      id: tag.id,
      name: tag.name,
      slug: tag.slug,
      description: tag.description,
      featuredImage: tag.feature_image,
    };
  }

  /**
   * Maps a Ghost posts response to our domain ArticleListResponse model.
   *
   * Transforms the complete Ghost API response containing posts and pagination data
   * into our standardized ArticleListResponse interface. This includes mapping all posts
   * to articles and preserving pagination metadata.
   *
   * @param response - The Ghost posts response containing posts array and pagination info
   * @returns ArticleListResponse object with mapped articles and pagination data
   */
  private mapPostsResponseToArticleList(
    response: GhostPostsResponse
  ): ArticleListResponse {
    const articles = (response?.posts ?? [])
      .map((post) => this.mapPostToArticle(post))
      .filter((article): article is Article => article !== null);

    return {
      articles,
      pagination: response?.pagination ?? this.getEmptyPagination(),
    };
  }

  /**
   * Returns default empty pagination when API response is missing pagination data.
   *
   * @returns Empty pagination object with safe defaults
   */
  private getEmptyPagination(): GhostPagination {
    return {
      page: 1,
      pages: 0,
      limit: 0,
      total: 0,
      prev: null,
      next: null,
    };
  }
}
